/* ============================================================
   ww-hover-image.js - second product photo on hover, in any product grid
   (collections, search, mounted kits), including grids Convermax renders.

   Convermax builds the cards client-side and only knows each product's first
   image, so this script gets the second one from Shopify itself
   (/products/<handle>.js) the first time a card is hovered, caches the answer
   for the session, and fades the photo in over the original.

   It never depends on Convermax's markup beyond "a card links to one product
   and has an <img>". Nothing inside the card is changed or removed: one extra
   <img> is laid on top, so a Convermax re-render can't break (or be broken by) it.
   Touch devices are skipped: there is no hover there.
   ============================================================ */
(function () {
  'use strict';
  if (window.__wwHoverImage) return;
  window.__wwHoverImage = true;
  if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  var CACHE_KEY = 'wwHoverImg:v1';
  var OVERLAY_CLASS = 'ww-hover-img';
  var SKIP_INSIDE = 'header, footer, nav, #cart, .cart, .mini_cart, .product__images, .product-gallery, [data-ww-shop], .ww-tabs';
  var KNOWN_CARDS = '.cmTemplate_product, .product-item';

  var cache = {};
  try { cache = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}') || {}; } catch (e) {}
  var inflight = {};
  var current = null;

  function saveCache() {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) {}
  }

  function handleOf(a) {
    var m = (a.getAttribute('href') || '').match(/\/products\/([^\/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function handlesIn(el) {
    var seen = {}, list = [];
    var as = el.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < as.length; i++) {
      var h = handleOf(as[i]);
      if (h && !seen[h]) { seen[h] = 1; list.push(h); }
    }
    if (el.matches && el.matches('a[href*="/products/"]')) {
      var own = handleOf(el);
      if (own && !seen[own]) list.push(own);
    }
    return list;
  }

  /* The card = the biggest ancestor that still links to exactly one product. */
  function findCard(target) {
    if (!target || !target.closest || target.closest(SKIP_INSIDE)) return null;
    var known = target.closest(KNOWN_CARDS);
    if (known && handlesIn(known).length === 1) return known;

    var card = null, node = target;
    for (var depth = 0; node && node !== document.body && depth < 8; depth++, node = node.parentElement) {
      var n = handlesIn(node).length;
      if (n === 1) card = node;
      else if (n > 1) break;
    }
    return card;
  }

  function mainImage(card) {
    var imgs = card.querySelectorAll('img');
    var best = null, bestArea = 0;
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.classList.contains(OVERLAY_CLASS)) continue;
      var r = img.getBoundingClientRect();
      var area = r.width * r.height;
      if (r.width >= 80 && area > bestArea) { best = img; bestArea = area; }
    }
    return best;
  }

  function sized(url, cssWidth) {
    var w = Math.min(1200, Math.ceil((cssWidth * (window.devicePixelRatio || 1)) / 100) * 100);
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  /* → Promise<string>: URL of the product's 2nd image, '' when it only has one */
  function secondImage(handle) {
    if (Object.prototype.hasOwnProperty.call(cache, handle)) return Promise.resolve(cache[handle]);
    if (inflight[handle]) return inflight[handle];
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    inflight[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var url = p && p.images && p.images.length > 1 ? String(p.images[1]) : '';
        cache[handle] = url;
        saveCache();
        return url;
      })
      .catch(function () { return ''; })
      .then(function (url) { delete inflight[handle]; return url; });
    return inflight[handle];
  }

  function place(overlay, img) {
    /* sit exactly over the original image, whatever the card's layout is */
    var host = img.offsetParent || img.parentElement;
    var hr = host.getBoundingClientRect(), ir = img.getBoundingClientRect();
    overlay.style.left = (ir.left - hr.left - host.clientLeft + host.scrollLeft) + 'px';
    overlay.style.top = (ir.top - hr.top - host.clientTop + host.scrollTop) + 'px';
    overlay.style.width = ir.width + 'px';
    overlay.style.height = ir.height + 'px';
  }

  function show(card) {
    var handle = handlesIn(card)[0];
    var img = mainImage(card);
    if (!handle || !img) return;
    /* Turbo's own cards already swap to a .secondary image */
    if (card.querySelector('img.secondary')) return;

    secondImage(handle).then(function (url) {
      if (!url || current !== card || !document.contains(img)) return;

      var overlay = card.querySelector('img.' + OVERLAY_CLASS);
      /* Convermax recycles card elements between products: never trust an old overlay */
      if (overlay && overlay.getAttribute('data-handle') !== handle) { overlay.remove(); overlay = null; }

      if (!overlay) {
        var host = img.offsetParent || img.parentElement;
        if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
        overlay = document.createElement('img');
        overlay.className = OVERLAY_CLASS;
        overlay.alt = '';
        overlay.setAttribute('aria-hidden', 'true');
        overlay.setAttribute('data-handle', handle);
        overlay.decoding = 'async';
        /* right after the original, so sale / stock badges that come later still paint on top */
        img.insertAdjacentElement('afterend', overlay);
        overlay.addEventListener('load', function () {
          if (current === card) overlay.classList.add('is-on');
        });
        overlay.addEventListener('error', function () { cache[handle] = ''; saveCache(); overlay.remove(); });
        place(overlay, img);
        overlay.src = sized(url, img.getBoundingClientRect().width);
        return;
      }
      place(overlay, img);
      if (overlay.complete && overlay.naturalWidth) overlay.classList.add('is-on');
    });
  }

  function hide(card) {
    var overlay = card && card.querySelector('img.' + OVERLAY_CLASS);
    if (overlay) overlay.classList.remove('is-on');
  }

  document.addEventListener('pointerover', function (e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    var card = findCard(e.target);
    if (card === current) return;
    if (current) hide(current);
    current = card;
    if (card) show(card);
  }, { passive: true });

  document.addEventListener('pointerout', function (e) {
    if (!current) return;
    if (e.relatedTarget && current.contains(e.relatedTarget)) return;
    hide(current);
    current = null;
  }, { passive: true });

  var css = document.createElement('style');
  css.textContent =
    '.' + OVERLAY_CLASS + '{position:absolute;margin:0!important;max-width:none!important;max-height:none!important;' +
    'object-fit:contain;background:#fff;opacity:0;transition:opacity .18s ease;pointer-events:none}' +
    '.' + OVERLAY_CLASS + '.is-on{opacity:1}' +
    '@media (prefers-reduced-motion:reduce){.' + OVERLAY_CLASS + '{transition:none}}';
  document.head.appendChild(css);
})();
